export function createCodeAnalyzerWorker() {
  const workerCode = `
// ===== SECURITY CONSTANTS =====
const SECURITY_CONFIG = {
  MAX_FILES: 5000,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB per file
  MAX_TOTAL_SIZE: 500 * 1024 * 1024, // 500MB total
  ALLOWED_EXTENSIONS: [
    '.js', '.ts', '.py', '.java', '.html', '.css', '.scss', '.vue', 
    '.json', '.md', '.xml', '.yaml', '.yml', '.sh', '.c', '.cpp', 
    '.cs', '.go', '.php', '.rb', '.txt', '.rs', '.swift', '.kt',
    '.zip', '.7z', '.tar.gz'
  ]
};
// ===== ERROR DETECTION CONSTANTS =====
const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
};

// ===== SAFE REGEX PATTERNS - ALL BACKSLASHES DOUBLE-ESCAPED =====
const COMMENT_PATTERNS = {
  '.js': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.ts': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.py': /#.*$/gm,
  '.java': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.html': /<!--[\\s\\S]*?-->/g,
  '.css': /\\/\\*[\\s\\S]*?\\*\\//g,
  '.vue': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/|<!--[\\s\\S]*?-->/g,
  '.php': /\\/\\/[^\\n]*|#.*$|\\/\\*[\\s\\S]*?\\*\\//g,
  '.rb': /#.*$/g,
  '.go': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.sh': /#.*$/g,
  '.sql': /--.*$|\\/\\*[\\s\\S]*?\\*\\//g,
  '.cpp': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.c': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.cs': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.rs': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.swift': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.kt': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.scss': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.sass': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g,
  '.json': /\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\//g
};

// ===== DOCUMENTATION QUALITY ANALYSIS =====
function analyzeDocumentationQuality(code, ext) {
  if (!code) return { commentRatio: 0, docScore: 0, totalDocs: 0, hasGoodDocs: false };
  
  let docScore = 0;
  let totalDocs = 0;
  
  // Count comment lines
  const commentLines = (code.match(/\\/\\/|#|<!--|\\/\\*/g) || []).length;
  const totalLines = code.split('\\n').length;
  
  // Calculate comment ratio (what percentage of lines are comments)
  const commentRatio = totalLines > 0 ? (commentLines / totalLines) * 100 : 0;
  
  // For JavaScript/TypeScript - JSDoc comments
  if (ext === '.js' || ext === '.ts' || ext === '.jsx' || ext === '.tsx') {
    const jsdocComments = (code.match(/\\/\\*\\*[\\s\\S]*?\\*\\//g) || []).length;
    docScore += jsdocComments * 2; // Weight JSDoc higher
    totalDocs += jsdocComments;
  }
  
  // For Python - docstrings
  if (ext === '.py') {
    const docstrings = (code.match(/""".*?"""|'''.*?'''/gs) || []).length;
    docScore += docstrings * 2;
    totalDocs += docstrings;
  }
  
  // For Java/C#/C++ - Javadoc style comments
  if (ext === '.java' || ext === '.cs' || ext === '.cpp' || ext === '.c') {
    const javadocComments = (code.match(/\\/\\*\\*[\\s\\S]*?\\*\\//g) || []).length;
    docScore += javadocComments * 2;
    totalDocs += javadocComments;
  }
  
  // For HTML/CSS - detect comments
  if (ext === '.html' || ext === '.css' || ext === '.scss') {
    const htmlComments = (code.match(/<!--[\\s\\S]*?-->/g) || []).length;
    const cssComments = (code.match(/\\/\\*[\\s\\S]*?\\*\\//g) || []).length;
    docScore += (htmlComments + cssComments);
    totalDocs += (htmlComments + cssComments);
  }
  
  return {
    commentRatio: parseFloat(commentRatio.toFixed(2)),
    docScore,
    totalDocs,
    hasGoodDocs: commentRatio > 15 || docScore > 0 // Arbitrary threshold
  };
}

// ===== SINGLE onmessage HANDLER =====
self.onmessage = function(event) {
  // Validate message structure
  if (!event.data || typeof event.data !== 'object') {
    self.postMessage({ type: 'error', error: 'Invalid message format' });
    return;
  }
  
  const { type, data } = event.data;
  
  if (!type || typeof type !== 'string') {
    self.postMessage({ type: 'error', error: 'Missing message type' });
    return;
  }
  
  if (type === 'analyze') {
    try {
      self.postMessage({
        type: 'debug',
        message: 'Worker received analysis request'
      });
      
      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid analysis data');
      }
      
      if (!Array.isArray(data.files)) {
        throw new Error('Files must be an array');
      }
      
      // Enforce file limits
      if (data.files.length > SECURITY_CONFIG.MAX_FILES) {
        throw new Error(\`Too many files (max \${SECURITY_CONFIG.MAX_FILES})\`);
      }
      
      // Filter and validate files
      const validFiles = [];
      let totalSize = 0;
      let skippedCount = 0;
      
      for (const file of data.files) {
        if (!file || typeof file.name !== 'string' || typeof file.content !== 'string') {
          skippedCount++;
          continue;
        }
        
        // Check file size
        if (file.content.length > SECURITY_CONFIG.MAX_FILE_SIZE) {
          self.postMessage({ 
            type: 'debug', 
            message: \`Skipping \${file.name}: too large (\${file.content.length} bytes)\` 
          });
          skippedCount++;
          continue;
        }
        
        totalSize += file.content.length;
        if (totalSize > SECURITY_CONFIG.MAX_TOTAL_SIZE) {
          self.postMessage({ 
            type: 'debug', 
            message: \`Total size limit reached, stopping at \${validFiles.length} files\` 
          });
          break;
        }
        
        // Only skip completely empty files
        if (file.content.length > 0) {
          validFiles.push(file);
        } else {
          skippedCount++;
        }
      }
      
      self.postMessage({
        type: 'debug',
        message: \`Worker analyzing \${validFiles.length} valid files (skipped \${skippedCount})\`
      });
      
      if (validFiles.length === 0) {
        throw new Error('No valid files to analyze');
      }
      
      const results = analyzeFiles(validFiles, data.selectedTypes || [], data.options || {});
      
      self.postMessage({
        type: 'complete',
        results: results
      });
    } catch (error) {
      self.postMessage({
        type: 'error',
        error: error.message || 'Analysis failed'
      });
    }
  }
};

// Add this function inside the worker blob
// ===== COMPLEXITY CALCULATION =====
function calculateComplexity(code, ext) {
  if (!code) return { cyclomatic: 1, nesting: 0 };
  
  let cyclomatic = 1;
  let nesting = 0;
  
  // FIXED: Properly escape all backslashes for string context
  const ifP = new RegExp('\\\\bif\\\\b', 'g');
  const elseIfP = new RegExp('\\\\belse if\\\\b', 'g');
  const caseP = new RegExp('\\\\bcase\\\\b', 'g');
  const catchP = new RegExp('\\\\bcatch\\\\b', 'g');
  const andP = new RegExp('&&', 'g');
  const orP = new RegExp('\\\\|\\\\|', 'g');
  const ternP = new RegExp('\\\\?[^:]', 'g');
  const forP = new RegExp('\\\\bfor\\\\b', 'g');
  const whileP = new RegExp('\\\\bwhile\\\\b', 'g');
  const elifP = new RegExp('\\\\belif\\\\b', 'g');
  const bandP = new RegExp('\\\\band\\\\b', 'g');
  const borP = new RegExp('\\\\bor\\\\b', 'g');
  const exceptP = new RegExp('\\\\bexcept\\\\b', 'g');

  const branchPatterns = {
    '.js': [ifP, elseIfP, caseP, catchP, andP, orP, ternP, forP, whileP],
    '.ts': [ifP, elseIfP, caseP, catchP, andP, orP, ternP, forP, whileP],
    '.py': [ifP, elifP, forP, whileP, bandP, borP, exceptP],
    '.java': [ifP, caseP, catchP, andP, orP, forP, whileP]
  };

  const patterns = branchPatterns[ext] || branchPatterns['.js'];
  if (patterns) {
    patterns.forEach(function(p) {
      var matches = code.match(p);
      if (matches) {
        cyclomatic += matches.length;
      }
    });
  }

  if (ext === '.py') {
    var maxDepth = 0, currentDepth = 0, lastIndent = 0;
    var lines = code.split('\\n');
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line.trim() || line.trim().startsWith('#')) continue;

      // Count leading spaces/tabs directly
      var indent = 0;
      while (indent < line.length && (line[indent] === ' ' || line[indent] === '\\t')) {
        indent++;
      }

      if (indent > lastIndent) currentDepth++;
      else if (indent < lastIndent) currentDepth = Math.max(0, currentDepth - 1);
      lastIndent = indent;
      maxDepth = Math.max(maxDepth, currentDepth);
    }
    nesting = maxDepth;
  }

  return { cyclomatic: cyclomatic, nesting: nesting };
}

// ===== MAIN ANALYSIS FUNCTION =====
function analyzeFiles(files, selectedTypes, options) {
  self.postMessage({
    type: 'debug',
    message: 'Worker starting analysis of ' + files.length + ' files'
  });
  
  var results = {
    totalLines: 0,
    totalFilesProcessed: 0,
    fileTypeSummary: {},
    allFiles: [],
    languageStats: [],
    languageFeatures: {
      topImports: {},
      todoComments: []
    },
    errorSummary: { totalErrors: 0, totalWarnings: 0, byType: {} },
    complexityMetrics: {
      avgComplexity: 0,
      maxComplexity: 0,
      totalFunctions: 0,
      totalClasses: 0
    },
    complexFiles: [],
    languageComplexity: []
  };
  
  // Track language stats
  var languageStatsMap = {};
  var topImportsMap = {};
  var todoComments = [];
  
  for (var i = 0; i < files.length; i++) {
    var file = files[i];
    var extension = getFileExtension(file.name);
    var language = getLanguageFromExt(extension);
    var complexity = calculateComplexity(file.content, extension);
    var funcCount = countFunctions(file.content, extension);
    var classCount = countClasses(file.content, extension);
    
    // FIXED: Properly declare docQuality with var and assign based on option
    var docQuality;
    if (options && options.analyzeDocumentation) {
      docQuality = analyzeDocumentationQuality(file.content, extension);
    } else {
      docQuality = { commentRatio: 0, docScore: 0, totalDocs: 0, hasGoodDocs: false };
    }
    
    if (i % 20 === 0) {
      self.postMessage({
        type: 'debug',
        message: 'Processing file ' + (i + 1) + ' of ' + files.length + ': ' + file.name
      });
    }
    
    // Check if file type should be analyzed
    var shouldAnalyze = false;
    for (var j = 0; j < selectedTypes.length; j++) {
      var type = selectedTypes[j];
      if (type.startsWith('.')) {
        if (extension === type) {
          shouldAnalyze = true;
          break;
        }
      } else {
        if (file.name.includes(type)) {
          shouldAnalyze = true;
          break;
        }
      }
    }
    
    if (shouldAnalyze && file.content) {
      var lines = countLines(file.content, options, extension);
      
      // ===== ENHANCED ERROR DETECTION =====
      var errorResult = enhancedErrorDetection(file.content, extension, file.name);
      var errors = errorResult.errors;
      var warnings = errorResult.warnings;
      
      results.totalLines += lines;
      results.totalFilesProcessed++;
      
      var fileData = {
        name: file.name,
        lines: lines,
        size: file.size || file.content.length,
        extension: extension,
        content: file.content,
        errors: errors,
        warnings: warnings,
        docQuality: docQuality,
        // NEW: store complexity on the file itself
        complexity: complexity.cyclomatic,
        nesting: complexity.nesting,
        functions: funcCount,
        classes: classCount
      };
      
      results.allFiles.push(fileData);
      
      // Track total errors/warnings
      results.errorSummary.totalErrors += errors.length;
      results.errorSummary.totalWarnings += warnings.length;
      
      // Group by error type
      var allIssues = errors.concat(warnings);
      for (var k = 0; k < allIssues.length; k++) {
        var issue = allIssues[k];
        var key = issue.type + ':' + issue.message;
        if (!results.errorSummary.byType[key]) {
          results.errorSummary.byType[key] = {
            message: issue.message,
            type: issue.type,
            severity: issue.severity,
            count: 0,
            files: []
          };
        }
        results.errorSummary.byType[key].count++;
        if (results.errorSummary.byType[key].files.indexOf(file.name) === -1) {
          results.errorSummary.byType[key].files.push(file.name);
        }
      }
      
      // Update file type summary
      if (!results.fileTypeSummary[extension]) {
        results.fileTypeSummary[extension] = {
          type: extension,
          count: 0,
          lines: 0
        };
      }
      results.fileTypeSummary[extension].count++;
      results.fileTypeSummary[extension].lines += lines;
      
      // ===== LANGUAGE ANALYSIS =====
      if (!languageStatsMap[language]) {
        languageStatsMap[language] = {
          language: language,
          files: 0,
          lines: 0,
          functions: 0,
          classes: 0,
          imports: 0
        };
      }
      
      var stats = languageStatsMap[language];
      stats.files++;
      stats.lines += lines;
      
      // Count functions and classes
      stats.functions += funcCount;
      stats.classes += classCount;
      
      // Extract imports
      var imports = extractImports(file.content, extension);
      stats.imports += imports.length;
      
      // Track top imports
      if (!topImportsMap[language]) topImportsMap[language] = {};
      for (var m = 0; m < imports.length; m++) {
        var imp = imports[m];
        if (!topImportsMap[language][imp]) {
          topImportsMap[language][imp] = 0;
        }
        topImportsMap[language][imp]++;
      }
      
      // Extract TODO/FIXME comments
      var todos = extractTodoComments(file.content, file.name);
      for (var n = 0; n < todos.length; n++) {
        todoComments.push(todos[n]);
      }
    }
    
    // Send progress update
    if (i % 10 === 0) {
      self.postMessage({
        type: 'progress',
        progress: Math.round((i / files.length) * 100),
        currentFile: file.name,
        processedFiles: i + 1,
        totalFiles: files.length
      });
    }
  }
  
  // Convert to arrays and sort
  results.fileTypeSummary = Object.values(results.fileTypeSummary);
  results.fileTypeSummary.sort(function(a, b) { return b.lines - a.lines; });
  
  // Sort all files by lines
  results.allFiles.sort(function(a, b) { return b.lines - a.lines; });
  
  // Prepare language stats
  results.languageStats = Object.values(languageStatsMap);
  results.languageStats.sort(function(a, b) { return b.lines - a.lines; });
  
  // Prepare top imports
  var sortedImports = {};
  var langKeys = Object.keys(topImportsMap);
  for (var p = 0; p < langKeys.length; p++) {
    var lang = langKeys[p];
    var importEntries = [];
    var importKeys = Object.keys(topImportsMap[lang]);
    for (var q = 0; q < importKeys.length; q++) {
      var name = importKeys[q];
      importEntries.push({ name: name, count: topImportsMap[lang][name] });
    }
    importEntries.sort(function(a, b) { return b.count - a.count; });
    sortedImports[lang] = importEntries;
  }
  
  results.languageFeatures = {
    topImports: sortedImports,
    todoComments: todoComments.sort(function(a, b) { 
      if (a.file < b.file) return -1;
      if (a.file > b.file) return 1;
      return 0;
    })
  };

  // After the file loop, before dependency analysis
  var totalComplexity = 0;
  var maxComplexity = 0;
  var totalFunctions = 0;
  var totalClasses = 0;
  var languageComplexityMap = {};

  for (var r = 0; r < results.allFiles.length; r++) {
    var file = results.allFiles[r];
    totalComplexity += file.complexity;
    maxComplexity = Math.max(maxComplexity, file.complexity);
    totalFunctions += file.functions;
    totalClasses += file.classes;
    
    var lang = getLanguageFromExt(file.extension);
    if (!languageComplexityMap[lang]) {
      languageComplexityMap[lang] = { language: lang, totalComplexity: 0, maxComplexity: 0, files: 0, functions: 0 };
    }
    var lc = languageComplexityMap[lang];
    lc.totalComplexity += file.complexity;
    lc.maxComplexity = Math.max(lc.maxComplexity, file.complexity);
    lc.files++;
    lc.functions += file.functions;
  }

  results.complexityMetrics = {
    avgComplexity: results.allFiles.length > 0 ? totalComplexity / results.allFiles.length : 0,
    maxComplexity: maxComplexity,
    totalFunctions: totalFunctions,
    totalClasses: totalClasses
  };

  results.complexFiles = [];
  for (var s = 0; s < results.allFiles.length; s++) {
    results.complexFiles.push(results.allFiles[s]);
  }
  results.complexFiles.sort(function(a, b) { return b.complexity - a.complexity; });

  var langComplexityArray = [];
  var langCompKeys = Object.keys(languageComplexityMap);
  for (var t = 0; t < langCompKeys.length; t++) {
    var lc = languageComplexityMap[langCompKeys[t]];
    langComplexityArray.push({
      language: lc.language,
      avgComplexity: lc.files > 0 ? lc.totalComplexity / lc.files : 0,
      maxComplexity: lc.maxComplexity,
      functions: lc.functions
    });
  }
  results.languageComplexity = langComplexityArray;
  
  // ===== DEPENDENCY ANALYSIS =====
  if (options && options.analyzeDependencies) {
    self.postMessage({
      type: 'debug',
      message: 'Building dependency graph...'
    });
    
    try {
      var dependencyResults = buildDependencyGraph(results.allFiles);
      results.dependencyGraph = dependencyResults.graph;
      results.dependencyTree = dependencyResults.tree;
      results.circularDependencies = dependencyResults.circular;
      
      self.postMessage({
        type: 'debug',
        message: 'Dependency graph built: ' + Object.keys(dependencyResults.graph).length + ' files, ' + 
                dependencyResults.circular.length + ' circular dependencies'
      });
    } catch (error) {
      self.postMessage({
        type: 'debug',
        message: 'Dependency analysis failed: ' + error.message
      });
      results.dependencyGraph = {};
      results.dependencyTree = [];
      results.circularDependencies = [];
    }
  } else {
    results.dependencyGraph = {};
    results.dependencyTree = [];
    results.circularDependencies = [];
  }
  
  self.postMessage({
    type: 'debug',
    message: 'Analysis complete: ' + results.totalFilesProcessed + ' files processed, ' + results.totalLines + ' total lines'
  });

  return results;
}

// ===== ENHANCED ERROR DETECTION FEATURE =====
function enhancedErrorDetection(code, ext, fileName) {
  const errors = [];
  const warnings = [];
  
  if (!code) return { errors, warnings };
  
  const lines = code.split('\\n');
  
  switch(ext) {
    case '.js':
    case '.ts':
    case '.jsx':
    case '.tsx':
      // ===== JAVASCRIPT/TYPESCRIPT ENHANCED DETECTION =====
      
      // 1. Missing semicolons (configurable)
      lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (trimmed && !trimmed.endsWith('{') && !trimmed.endsWith('}') && 
            !trimmed.endsWith(';') && !trimmed.startsWith('//') && 
            !trimmed.startsWith('/*') && !trimmed.endsWith(':') &&
            !trimmed.includes('function') && !trimmed.includes('if') && 
            !trimmed.includes('else') && !trimmed.includes('for') && 
            !trimmed.includes('while') && !trimmed.includes('switch') &&
            !trimmed.includes('try') && !trimmed.includes('catch') &&
            !trimmed.includes('=>') && !trimmed.endsWith('(') &&
            !trimmed.endsWith(')') && !trimmed.endsWith(',') &&
            !trimmed.endsWith('[') && !trimmed.endsWith(']')) {
          warnings.push({
            type: 'warning',
            message: 'Possible missing semicolon',
            line: index + 1,
            column: line.length,
            severity: 'low'
          });
        }
      });
      
      // 2. ENHANCED: Unused variables with sophisticated detection
      // First, collect all variable declarations with their scope context
      const variableDeclarations = [];
      
      // Match let, const, var declarations - FIXED: Proper character class
      const declRegex = /\\b(?:let|const|var)\\s+([a-zA-Z0-9_$]+)\\s*[=;]/g;
      let match;
      while ((match = declRegex.exec(code)) !== null) {
        variableDeclarations.push({
          name: match[1],
          index: match.index,
          line: findLineNumber(code, match[1], match.index)
        });
      }
      
      // Also catch destructured variables: const { x, y } = obj;
      const destructureRegex = /\\b(?:let|const|var)\\s*{\\s*([^}]+)\\s*}\\s*=/g;
      while ((match = destructureRegex.exec(code)) !== null) {
        const vars = match[1].split(',').map(v => v.trim());
        vars.forEach(v => {
          // Handle aliased destructuring: { x: newName }
          if (v.includes(':')) {
            const parts = v.split(':');
            const varName = parts[1].trim();
            if (varName.match(/^[a-zA-Z0-9_$]+$/)) {
              variableDeclarations.push({
                name: varName,
                index: match.index,
                line: findLineNumber(code, varName, match.index)
              });
            }
          } else {
            if (v.match(/^[a-zA-Z0-9_$]+$/)) {
              variableDeclarations.push({
                name: v,
                index: match.index,
                line: findLineNumber(code, v, match.index)
              });
            }
          }
        });
      }
      
      // Also catch function parameters
      const paramRegex = /function\\s*\\w*\\s*\\(([^)]*)\\)/g;
      while ((match = paramRegex.exec(code)) !== null) {
        if (match[1] && match[1].trim()) {
          const params = match[1].split(',').map(p => p.trim());
          params.forEach(p => {
            // Remove default values
            const paramName = p.split('=')[0].trim();
            if (paramName && paramName.match(/^[a-zA-Z0-9_$]+$/)) {
              variableDeclarations.push({
                name: paramName,
                index: match.index,
                line: findLineNumber(code, paramName, match.index)
              });
            }
          });
        }
      }
      
      // Also catch arrow function parameters
      const arrowParamRegex = /\\(([^)]*)\\)\\s*=>/g;
      while ((match = arrowParamRegex.exec(code)) !== null) {
        if (match[1] && match[1].trim()) {
          const params = match[1].split(',').map(p => p.trim());
          params.forEach(p => {
            if (p && p.match(/^[a-zA-Z0-9_$]+$/)) {
              variableDeclarations.push({
                name: p,
                index: match.index,
                line: findLineNumber(code, p, match.index)
              });
            }
          });
        }
      }
      
      // Now check each variable for usage with advanced pattern matching
      variableDeclarations.forEach(decl => {
        const varName = decl.name;
        if (!varName || varName.length === 0) return;
        
        // Skip common loop variables and special cases
        if (['i', 'j', 'k', 'idx', 'index', 'e', 'err', 'error', '_'].includes(varName)) {
          return; // These are often intentionally unused
        }
        
        // Create a regex that matches the variable name in various contexts
        // Use a simpler approach to avoid regex complexity issues
        
        // First, remove comments to avoid false positives
        const codeWithoutComments = code.replace(/\\/\\/.*|\\/\\*[\\s\\S]*?\\*\\//g, '');
        
        // Check for various usage patterns
        let isUsed = false;
        
        // Check for basic usage with word boundaries
        const basicPattern = new RegExp('\\\\b' + varName + '\\\\b', 'g');
        const basicMatches = codeWithoutComments.match(basicPattern) || [];
        
        // Count how many times it appears in declarations
        const declCount = (codeWithoutComments.match(new RegExp('\\\\b(?:let|const|var)\\\\s+' + varName + '\\\\b', 'g')) || []).length;
        
        if (basicMatches.length > declCount) {
          isUsed = true;
        }
        
        // Check for negation
        if (!isUsed) {
          const negPattern = new RegExp('!' + varName + '\\\\b', 'g');
          if (codeWithoutComments.match(negPattern)) isUsed = true;
        }
        
        // Check for logical operations
        if (!isUsed) {
          const logicalPattern = new RegExp('\\\\b' + varName + '\\\\s*(?:&&|\\\\|\\\\||\\\\?)', 'g');
          if (codeWithoutComments.match(logicalPattern)) isUsed = true;
        }
        
        // Check for comparisons
        if (!isUsed) {
          const compPattern = new RegExp('\\\\b' + varName + '\\\\s*(?:==|===|!=|!==|>|<|>=|<=)', 'g');
          if (codeWithoutComments.match(compPattern)) isUsed = true;
        }
        
        // Check for math operations
        if (!isUsed) {
          const mathPattern = new RegExp('\\\\b' + varName + '\\\\s*(?:[+\\\\-\\\\*/%])', 'g');
          if (codeWithoutComments.match(mathPattern)) isUsed = true;
        }
        
        // Check for property access
        if (!isUsed) {
          const propPattern = new RegExp('\\\\b' + varName + '\\\\.', 'g');
          if (codeWithoutComments.match(propPattern)) isUsed = true;
        }
        
        // Check for function calls
        if (!isUsed) {
          const callPattern = new RegExp('\\\\b' + varName + '\\\\s*\\\\(', 'g');
          if (codeWithoutComments.match(callPattern)) isUsed = true;
        }
        
        // Check for increment/decrement
        if (!isUsed) {
          const incPattern = new RegExp('\\\\b' + varName + '\\\\s*(?:\\\\+\\\\+|\\\\-\\\\-)', 'g');
          if (codeWithoutComments.match(incPattern)) isUsed = true;
        }
        
        // Check for return statements
        if (!isUsed) {
          const returnPattern = new RegExp('return\\\\s+' + varName + '\\\\b', 'g');
          if (codeWithoutComments.match(returnPattern)) isUsed = true;
        }
        
        // Check for template literals
        if (!isUsed) {
          const templatePattern = new RegExp('\\\\$\\\\{' + varName + '\\\\}', 'g');
          if (codeWithoutComments.match(templatePattern)) isUsed = true;
        }
        
        // Check for JSX
        if (!isUsed) {
          const jsxPattern = new RegExp('{' + varName + '}', 'g');
          if (codeWithoutComments.match(jsxPattern)) isUsed = true;
        }
        
        if (!isUsed) {
          warnings.push({
            type: 'warning',
            message: \`Possibly unused variable '\${varName}'\`,
            line: decl.line,
            severity: 'medium'
          });
        }
      });
      
      // 3. Console.log statements - IMPROVED with exec loop
      const consoleRegex = /console\\.(?:log|warn|error|info|debug)\\s*\\(/g;
      let consoleMatch;
      while ((consoleMatch = consoleRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, consoleMatch[0], consoleMatch.index);
        warnings.push({
          type: 'warning',
          message: \`\${consoleMatch[0].replace('\\\\(', '()')} statement left in code\`,
          line: lineNum,
          severity: 'low'
        });
      }
      
      // 4. Debugger statements
      const debuggerRegex = /debugger;?/g;
      let debuggerMatch;
      while ((debuggerMatch = debuggerRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, debuggerMatch[0], debuggerMatch.index);
        errors.push({
          type: 'error',
          message: 'Debugger statement should be removed',
          line: lineNum,
          severity: 'medium'
        });
      }
      
      // 5. Empty catch blocks
      const emptyCatchRegex = /catch\\s*\\([^)]*\\)\\s*{\\s*}/g;
      let emptyCatchMatch;
      while ((emptyCatchMatch = emptyCatchRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, emptyCatchMatch[0], emptyCatchMatch.index);
        warnings.push({
          type: 'warning',
          message: 'Empty catch block',
          line: lineNum,
          severity: 'high'
        });
      }
      
      // 6. TODO/FIXME comments (as warnings)
      const todoRegex = /\\/\\/\\s*(TODO|FIXME|HACK|BUG|OPTIMIZE|XXX)/gi;
      let todoMatch;
      while ((todoMatch = todoRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, todoMatch[0], todoMatch.index);
        warnings.push({
          type: 'warning',
          message: \`\${todoMatch[0].trim()} comment found\`,
          line: lineNum,
          severity: 'low'
        });
      }
      
      // 7. Equality comparisons (== vs ===)
      const looseEqRegex = /[^=!]==[^=]/g;
      let looseEqMatch;
      while ((looseEqMatch = looseEqRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, looseEqMatch[0], looseEqMatch.index);
        warnings.push({
          type: 'warning',
          message: 'Use === instead of ==',
          line: lineNum,
          severity: 'medium'
        });
      }
      
      // 8. Nested ternaries (complexity)
      const ternaryCount = (code.match(/\\?/g) || []).length;
      if (ternaryCount > 3) {
        const lineNum = findLineNumber(code, '?', code.indexOf('?'));
        warnings.push({
          type: 'warning',
          message: 'Multiple ternary operators detected (consider refactoring)',
          line: lineNum,
          severity: 'low'
        });
      }
      
      // 9. Function complexity (too many parameters)
      const funcManyParamsRegex = /function\\s*\\w*\\s*\\(([^)]{20,})\\)/g;
      let funcManyParamsMatch;
      while ((funcManyParamsMatch = funcManyParamsRegex.exec(code)) !== null) {
        const lineNum = findLineNumber(code, funcManyParamsMatch[0], funcManyParamsMatch.index);
        warnings.push({
          type: 'warning',
          message: 'Function has many parameters (consider using an options object)',
          line: lineNum,
          severity: 'low'
        });
      }
      break;
      
case '.py':
  // ===== PYTHON ENHANCED DETECTION =====
  
  // 1. Missing docstrings in functions/classes
  const funcRegex = /def\\s+\\w+\\s*\\([^)]*\\)\\s*:/g;
  let funcMatch;
  while ((funcMatch = funcRegex.exec(code)) !== null) {
    const lineNum = findLineNumber(code, funcMatch[0], funcMatch.index);
    const nextLines = getNextLines(code, lineNum, 3);
    if (!nextLines.includes('"""') && !nextLines.includes("'''")) {
      warnings.push({
        type: 'warning',
        message: 'Function missing docstring',
        line: lineNum,
        severity: 'low'
      });
    }
  }
  
  // 2. Print statements (debug leftovers)
  const printRegex = /\\bprint\\s*\\(/g;
  let printMatch;
  while ((printMatch = printRegex.exec(code)) !== null) {
    const lineNum = findLineNumber(code, printMatch[0], printMatch.index);
    warnings.push({
      type: 'warning',
      message: 'Print statement left in code',
      line: lineNum,
      severity: 'low'
    });
  }
  
  // 3. Bare except clauses
  const bareExceptRegex = /except\\s*:/g;
  let bareExceptMatch;
  while ((bareExceptMatch = bareExceptRegex.exec(code)) !== null) {
    const lineNum = findLineNumber(code, bareExceptMatch[0], bareExceptMatch.index);
    errors.push({
      type: 'error',
      message: 'Bare except clause (catches all exceptions)',
      line: lineNum,
      severity: 'high'
    });
  }
  
  // 4. Comparison to None, True, False using ==
  const noneRegex = /\\b\\w+\\s*==\\s*None\\b/g;
  let noneMatch;
  while ((noneMatch = noneRegex.exec(code)) !== null) {
    const lineNum = findLineNumber(code, noneMatch[0], noneMatch.index);
    warnings.push({
      type: 'warning',
      message: 'Use "is None" instead of "== None"',
      line: lineNum,
      severity: 'medium'
    });
  }
  
  // 5. Unused variables in Python - ENHANCED for Django, Flask, and FastAPI frameworks
  const pyLines = code.split('\\n');
  const pyVars = [];
  
  // Framework-specific attributes that are used internally
  const frameworkAttributes = [
    // ===== DJANGO / DRF ATTRIBUTES =====
    // DRF View attributes
    'serializer_class', 'renderer_classes', 'permission_classes', 
    'authentication_classes', 'queryset', 'lookup_field', 'lookup_url_kwarg',
    'pagination_class', 'filter_backends', 'parser_classes', 'throttle_classes',
    'content_negotiation_class', 'metadata_class', 'versioning_class',
    'allowed_methods', 'http_method_names',
    
    // DRF ModelViewSet attributes
    'model', 'fields', 'exclude', 'read_only_fields', 'extra_kwargs', 
    'depth', 'many', 'context', 'partial', 'instance', 'data',
    
    // Django model Meta class attributes
    'db_table', 'ordering', 'verbose_name', 'verbose_name_plural',
    'unique_together', 'index_together', 'constraints', 'indexes',
    
    // Django settings
    'allowed_schemes', 'allowed_hosts', 'installed_apps', 'middleware',
    'databases', 'caches', 'templates', 'auth_password_validators',
    'auth_user_model', 'login_url', 'login_redirect_url', 'logout_redirect_url',
    'static_url', 'media_url', 'static_root', 'media_root',
    'staticfiles_dirs', 'templates_dirs', 'fixture_dirs',
    'secret_key', 'debug', 'time_zone', 'language_code', 'use_i18n', 'use_l10n',
    'use_tz', 'apps', 'site_id', 'root_urlconf', 'wsgi_application',
    
    // Django REST Framework settings
    'default_authentication_classes', 'default_permission_classes',
    'default_renderer_classes', 'default_parser_classes',
    'default_throttle_classes', 'default_content_negotiation_class',
    'default_pagination_class', 'default_filter_backends',
    'default_version', 'allowed_versions', 'versioning_scheme',
    'unauthenticated_user', 'default_schema_class', 'default_router_class',
    
    // URL patterns
    'app_name', 'namespace', 'name', 'pattern', 'default_args',
    
    // Form attributes
    'widgets', 'labels', 'help_texts', 'error_messages', 'field_classes',
    
    // Admin attributes
    'list_display', 'list_filter', 'search_fields', 'date_hierarchy',
    'ordering', 'list_per_page', 'list_max_show_all', 'list_editable',
    'filter_horizontal', 'filter_vertical', 'raw_id_fields', 'prepopulated_fields',
    
    // ===== FLASK ATTRIBUTES =====
    // Flask app configuration
    'SECRET_KEY', 'DEBUG', 'TESTING', 'SESSION_COOKIE_NAME',
    'SESSION_COOKIE_DOMAIN', 'SESSION_COOKIE_PATH', 'SESSION_COOKIE_HTTPONLY',
    'SESSION_COOKIE_SECURE', 'SESSION_COOKIE_SAMESITE', 'PERMANENT_SESSION_LIFETIME',
    'SESSION_REFRESH_EACH_REQUEST', 'USE_X_SENDFILE', 'LOGGER_NAME',
    'LOGGER_HANDLER_POLICY', 'SERVER_NAME', 'APPLICATION_ROOT',
    'PREFERRED_URL_SCHEME', 'MAX_CONTENT_LENGTH', 'JSON_AS_ASCII',
    'JSON_SORT_KEYS', 'JSONIFY_PRETTYPRINT_REGULAR', 'JSONIFY_MIMETYPE',
    'TEMPLATES_AUTO_RELOAD', 'EXPLAIN_TEMPLATE_LOADING',
    
    // Flask extensions
    'SQLALCHEMY_DATABASE_URI', 'SQLALCHEMY_TRACK_MODIFICATIONS',
    'SQLALCHEMY_ENGINE_OPTIONS', 'SQLALCHEMY_ECHO',
    'SQLALCHEMY_POOL_SIZE', 'SQLALCHEMY_POOL_TIMEOUT',
    'SQLALCHEMY_POOL_RECYCLE', 'SQLALCHEMY_MAX_OVERFLOW',
    
    // Flask-Login
    'login_manager', 'login_view', 'login_message', 'login_message_category',
    'refresh_view', 'needs_refresh_message', 'needs_refresh_message_category',
    
    // Flask-WTF
    'WTF_CSRF_ENABLED', 'WTF_CSRF_SECRET_KEY', 'WTF_CSRF_TIME_LIMIT',
    'WTF_CSRF_SSL_STRICT', 'WTF_CSRF_METHODS', 'WTF_CSRF_FIELD_NAME',
    'WTF_CSRF_HEADERS', 'WTF_CSRF_CHECK_DEFAULT',
    
    // Flask-Mail
    'MAIL_SERVER', 'MAIL_PORT', 'MAIL_USE_TLS', 'MAIL_USE_SSL',
    'MAIL_DEBUG', 'MAIL_USERNAME', 'MAIL_PASSWORD', 'MAIL_DEFAULT_SENDER',
    'MAIL_MAX_EMAILS', 'MAIL_SUPPRESS_SEND', 'MAIL_ASCII_ATTACHMENTS',
    
    // Flask-Migrate
    'migrate', 'Migrate',
    
    // Flask-Caching
    'CACHE_TYPE', 'CACHE_DEFAULT_TIMEOUT', 'CACHE_KEY_PREFIX',
    'CACHE_THRESHOLD', 'CACHE_DIR', 'CACHE_OPTIONS', 'CACHE_ARGS',
    
    // Flask-Bcrypt
    'BCRYPT_LOG_ROUNDS', 'BCRYPT_HANDLE_LONG_PASSWORDS',
    
    // Flask-JWT / Flask-JWT-Extended
    'JWT_SECRET_KEY', 'JWT_ALGORITHM', 'JWT_DECODE_ALGORITHMS',
    'JWT_VERIFY_CLAIMS', 'JWT_REQUIRED_CLAIMS', 'JWT_ACCESS_TOKEN_EXPIRES',
    'JWT_REFRESH_TOKEN_EXPIRES', 'JWT_TOKEN_LOCATION', 'JWT_HEADER_NAME',
    'JWT_HEADER_TYPE', 'JWT_QUERY_STRING_NAME', 'JWT_JSON_KEY',
    'JWT_REFRESH_JSON_KEY', 'JWT_COOKIE_SECURE', 'JWT_COOKIE_CSRF_PROTECT',
    'JWT_COOKIE_DOMAIN', 'JWT_SESSION_COOKIE',
    
    // Flask-RESTful
    'bundle_errors', 'catch_all_404s', 'propagate_exceptions',
    
    // Flask-SocketIO
    'async_mode', 'logger', 'engineio_logger', 'cors_allowed_origins',
    'cors_credentials', 'always_connect', 'namespaces', 'path',
    'manage_session', 'json', 'message_queue', 'channel',
    
    // ===== FASTAPI ATTRIBUTES =====
    // FastAPI app configuration
    'title', 'description', 'version', 'openapi_url', 'docs_url',
    'redoc_url', 'swagger_ui_oauth2_redirect_url', 'swagger_ui_init_oauth',
    'openapi_prefix', 'root_path', 'root_path_in_servers', 'dependencies',
    'default_response_class', 'callbacks', 'routes', 'router',
    'include_in_schema', 'response_class', 'response_model', 'response_model_include',
    'response_model_exclude', 'response_model_by_alias', 'response_model_exclude_unset',
    'response_model_exclude_defaults', 'response_model_exclude_none',
    
    // FastAPI parameters
    'path', 'query', 'header', 'cookie', 'body', 'form', 'file',
    'depends', 'security', 'status_code', 'tags', 'summary', 'description',
    'response_description', 'operation_id', 'deprecated', 'methods',
    
    // FastAPI dependencies
    'Depends', 'Security', 'BackgroundTasks', 'Request', 'Response',
    'WebSocket', 'WebSocketDisconnect', 'WebSocketException',
    
    // FastAPI databases (SQLAlchemy)
    'SessionLocal', 'engine', 'Base', 'sessionmaker', 'declarative_base',
    
    // FastAPI middleware
    'middleware', 'CORSMiddleware', 'TrustedHostMiddleware',
    'GZipMiddleware', 'HTTPSRedirectMiddleware',
    
    // FastAPI exceptions
    'HTTPException', 'WebSocketException', 'RequestValidationError',
    'WebSocketRequestValidationError',
    
    // FastAPI testing
    'TestClient', 'AsyncClient',
    
    // Pydantic settings (used with FastAPI)
    'Config', 'orm_mode', 'allow_population_by_field_name',
    'arbitrary_types_allowed', 'validate_assignment', 'use_enum_values',
    
    // FastAPI Users
    'UserManager', 'UserDatabase', 'UserModel', 'UserCreate', 'UserUpdate',
    'UserDB', 'jwt_authentication', 'cookie_authentication',
    
    // FastAPI Pagination
    'Page', 'Params', 'paginate', 'LimitOffsetPage', 'LimitOffsetParams',
    
    // FastAPI Caching
    'cache', 'cached', 'CacheBackend', 'RedisBackend',
    
    // FastAPI Limiter
    'limiter', 'RateLimiter', 'RateLimitMiddleware',
    
    // Common Python patterns
    '__all__', '__version__', '__author__', '__license__',
    'app', 'application', 'wsgi_app', 'asgi_app', 'config'
  ];

  // Also match any attribute that follows common naming patterns
  const frameworkPatterns = [
    /^[A-Z][a-zA-Z0-9_]+$/,           // Class names (start with capital)
    /^[A-Z][A-Z0-9_]+$/,               // ALL_CAPS constants (settings)
    /^[a-z]+_[a-z]+_class$/,           // snake_case ending with _class
    /^[a-z]+_[a-z]+_classes$/,         // snake_case ending with _classes
    /^[a-z]+_[a-z]+_config$/,          // snake_case ending with _config
    /^[a-z]+_[a-z]+_settings$/,        // snake_case ending with _settings
    /^[a-z]+_[a-z]+_scheme$/,          // snake_case ending with _scheme
    /^[a-z]+_[a-z]+_schemes$/,         // snake_case ending with _schemes
    /^[a-z]+_[a-z]+_manager$/,         // snake_case ending with _manager
    /^[a-z]+_[a-z]+_backend$/,         // snake_case ending with _backend
    /^[a-z]+_[a-z]+_middleware$/,      // snake_case ending with _middleware
    /^[a-z]+_[a-z]+_exception$/,       // snake_case ending with _exception
    /^[a-z]+_[a-z]+_dependency$/,      // snake_case ending with _dependency
    /^[a-z]+_[a-z]+_router$/,          // snake_case ending with _router
    /^[a-z]+_[a-z]+_route$/,           // snake_case ending with _route
    /^default_[a-z_]+$/,                // default_* patterns
    /^JWT_[A-Z_]+$/,                    // JWT_* patterns
    /^SQLALCHEMY_[A-Z_]+$/,             // SQLALCHEMY_* patterns
    /^FLASK_[A-Z_]+$/,                  // FLASK_* patterns
    /^FASTAPI_[A-Z_]+$/,                // FASTAPI_* patterns
    /^[A-Z]+_[A-Z]+_URI$/,              // *_URI patterns (database URIs)
    /^[A-Z]+_[A-Z]+_URL$/               // *_URL patterns
  ];

  pyLines.forEach((line, idx) => {
    // Match variable assignments (including class attributes)
    const assignMatch = line.match(/^\\s*([a-zA-Z_][a-zA-Z0-9_]*)\\s*=/);
    if (assignMatch && !assignMatch[1].startsWith('_')) {
      const varName = assignMatch[1];
      let isFrameworkAttr = frameworkAttributes.includes(varName);
      
      // Check against patterns if not in exact list
      if (!isFrameworkAttr) {
        for (const pattern of frameworkPatterns) {
          if (pattern.test(varName)) {
            isFrameworkAttr = true;
            break;
          }
        }
      }
      
      pyVars.push({
        name: varName,
        line: idx + 1,
        isClassAttribute: line.includes('class ') || false,
        isFrameworkAttr: isFrameworkAttr
      });
    }
    
    // Also catch class-level attribute assignments without = (like serializer_class = RegisterStaffSerializer)
    const classAttrMatch = line.match(/^\\s*([a-zA-Z_][a-zA-Z0-9_]*)\\s+=\\s+[^=]+$/);
    if (classAttrMatch && !classAttrMatch[1].startsWith('_')) {
      const varName = classAttrMatch[1];
      let isFrameworkAttr = frameworkAttributes.includes(varName);
      
      // Check against patterns if not in exact list
      if (!isFrameworkAttr) {
        for (const pattern of frameworkPatterns) {
          if (pattern.test(varName)) {
            isFrameworkAttr = true;
            break;
          }
        }
      }
      
      pyVars.push({
        name: varName,
        line: idx + 1,
        isClassAttribute: true,
        isFrameworkAttr: isFrameworkAttr
      });
    }
  });
  
  // Check each variable for usage
  pyVars.forEach(varInfo => {
    const varName = varInfo.name;
    if (['_', 'self', 'cls'].includes(varName)) return;
    
    // Skip framework attributes - they're used by the framework internally
    if (varInfo.isFrameworkAttr) {
      return; // These are intentionally not referenced directly in code
    }
    
    // Skip class attributes that are clearly framework-specific patterns
    if (varInfo.isClassAttribute && (
      varName.includes('_class') || 
      varName.includes('_classes') || 
      varName.includes('_permissions') ||
      varName.includes('_config') ||
      varName.includes('_settings') ||
      varName.includes('_scheme') ||
      varName.includes('_manager') ||
      varName.includes('_backend') ||
      varName.includes('_middleware') ||
      varName.endsWith('_set') ||
      varName.endsWith('_url') ||
      varName.endsWith('_uri')
    )) {
      return; // These are likely framework configurations
    }
    
    // Remove the declaration line from consideration to avoid self-match
    const lines = code.split('\\n');
    let codeWithoutDeclaration = '';
    lines.forEach((line, idx) => {
      if (idx + 1 !== varInfo.line) {
        codeWithoutDeclaration += line + '\\n';
      }
    });
    
    // Check for various usage patterns including return statements
    const usagePatterns = [
      new RegExp('\\\\b' + varName + '\\\\b(?![\\\\s]*=)', 'g'),  // Not followed by = (assignment)
      new RegExp('return\\\\s+' + varName + '\\\\b', 'g'),        // Return statement
      new RegExp(varName + '\\\\s*=\\\\s*' + varName, 'g'),      // Self-assignment (x = x)
      new RegExp('\\\\(' + varName + '\\\\)', 'g'),               // In parentheses
      new RegExp('\\\\[' + varName + '\\\\]', 'g'),               // In brackets
      new RegExp('\\\\{' + varName + '\\\\}', 'g'),               // In braces
      new RegExp(varName + '\\\\.', 'g'),                         // Attribute access
      new RegExp(varName + '\\\\s*[+\\\\-\\\\*/%]=', 'g'),        // Augmented assignment
      new RegExp('self\\\\.' + varName + '\\\\b', 'g'),           // self.attribute access
      new RegExp('cls\\\\.' + varName + '\\\\b', 'g'),            // cls.attribute access
      new RegExp('app\\\\.' + varName + '\\\\b', 'g'),            // Flask/FastAPI app.attribute access
      new RegExp('request\\\\.' + varName + '\\\\b', 'g'),        // request.attribute access
      new RegExp('response\\\\.' + varName + '\\\\b', 'g'),       // response.attribute access
      new RegExp('session\\\\.' + varName + '\\\\b', 'g')         // session.attribute access
    ];
    
    let isUsed = false;
    for (const pattern of usagePatterns) {
      if (pattern.test(codeWithoutDeclaration)) {
        isUsed = true;
        break;
      }
    }
    
    // Special check for variables that are returned
    const returnPattern = new RegExp('return\\\\s+' + varName + '\\\\b', 'g');
    if (returnPattern.test(code)) {
      isUsed = true;
    }
    
    // Special check for FastAPI dependency injection
    const fastApiDepPattern = new RegExp('Depends\\\\(\\\\s*' + varName + '\\\\s*\\\\)', 'g');
    if (fastApiDepPattern.test(code)) {
      isUsed = true;
    }
    
    // Special check for Flask route decorators
    const flaskRoutePattern = new RegExp('@app\\\\.route\\\\(.*\\\\)\\\\s*\\\\ndef\\s+\\w+\\\\(.*' + varName + '.*\\\\)', 'g');
    if (flaskRoutePattern.test(code)) {
      isUsed = true;
    }
    
    if (!isUsed) {
      warnings.push({
        type: 'warning',
        message: \`Possibly unused variable '\${varName}'\`,
        line: varInfo.line,
        severity: 'medium'
      });
    }
  });
  break;
      
    case '.html':
      // ===== HTML ENHANCED DETECTION =====
      
      // 1. Unclosed tags
      const openTags = (code.match(/<\\w+[^>]*>/g) || []).filter(t => !t.startsWith('</') && !t.endsWith('/>'));
      const closeTags = (code.match(/<\\/\\w+>/g) || []);
      
      if (openTags.length !== closeTags.length) {
        warnings.push({
          type: 'warning',
          message: \`Possible unclosed tags: \${openTags.length} opening, \${closeTags.length} closing\`,
          line: 1,
          severity: 'medium'
        });
      }
      
      // 2. Missing alt attributes on images
      const imgRegex = /<img[^>]*>/g;
      let imgMatch;
      while ((imgMatch = imgRegex.exec(code)) !== null) {
        if (!imgMatch[0].includes('alt=')) {
          const lineNum = findLineNumber(code, imgMatch[0], imgMatch.index);
          warnings.push({
            type: 'warning',
            message: 'Image missing alt attribute',
            line: lineNum,
            severity: 'medium'
          });
        }
      }
      
      // 3. Duplicate IDs
      const idRegex = /id=['"]([^'"]+)['"]/g;
      const ids = {};
      let idMatch;
      while ((idMatch = idRegex.exec(code)) !== null) {
        const id = idMatch[1];
        if (ids[id]) {
          const lineNum = findLineNumber(code, idMatch[0], idMatch.index);
          errors.push({
            type: 'error',
            message: \`Duplicate ID '\${id}'\`,
            line: lineNum,
            severity: 'high'
          });
        } else {
          ids[id] = true;
        }
      }
      break;
      
    case '.css':
    case '.scss':
      // ===== CSS/SCSS ENHANCED DETECTION =====
      
      // 1. Duplicate selectors
      const selectorRegex = /[.#]?\\w+[\\s\\n]*{/g;
      const selectors = {};
      let selectorMatch;
      while ((selectorMatch = selectorRegex.exec(code)) !== null) {
        const selector = selectorMatch[0].replace(/[\\s\\n]*{/, '').trim();
        if (selectors[selector]) {
          const lineNum = findLineNumber(code, selectorMatch[0], selectorMatch.index);
          warnings.push({
            type: 'warning',
            message: \`Duplicate selector '\${selector}'\`,
            line: lineNum,
            severity: 'low'
          });
        } else {
          selectors[selector] = true;
        }
      }
      
      // 2. Invalid color values
      const colorRegex = /#[0-9A-Fa-f]{3,6}\\b/g;
      let colorMatch;
      while ((colorMatch = colorRegex.exec(code)) !== null) {
        const color = colorMatch[0];
        if (color.length === 4 && !/^#[0-9A-Fa-f]{3}$/.test(color)) {
          const lineNum = findLineNumber(code, color, colorMatch.index);
          errors.push({
            type: 'error',
            message: \`Invalid hex color '\${color}'\`,
            line: lineNum,
            severity: 'medium'
          });
        }
        if (color.length === 7 && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
          const lineNum = findLineNumber(code, color, colorMatch.index);
          errors.push({
            type: 'error',
            message: \`Invalid hex color '\${color}'\`,
            line: lineNum,
            severity: 'medium'
          });
        }
      }
      break;
  }
  
  return { errors, warnings };
}

// ===== DEPENDENCY GRAPH FUNCTIONS =====
function buildDependencyGraph(files) {
  const graph = {};
  const fileMap = {};
  
  // Create a map of all files by their names
  files.forEach(file => {
    fileMap[file.name] = file;
    graph[file.name] = {
      name: file.name,
      dependencies: [],
      dependents: [],
      size: file.size,
      lines: file.lines,
      extension: getFileExtension(file.name)
    };
  });
  
  // First pass: extract all dependencies
  files.forEach(file => {
    const ext = getFileExtension(file.name);
    const dependencies = extractAllDependencies(file.content, ext, file.name, files);
    
    // Resolve each dependency to an actual file path
    const resolvedDeps = dependencies.map(dep => {
      return resolveDependencyPath(file.name, dep, fileMap, ext);
    }).filter(dep => dep && dep !== file.name);
    
    // Add unique dependencies to the graph
    const uniqueDeps = [...new Set(resolvedDeps)];
    graph[file.name].dependencies = uniqueDeps;
  });
  
  // Second pass: build dependents
  Object.keys(graph).forEach(fileName => {
    graph[fileName].dependencies.forEach(dep => {
      if (graph[dep]) {
        if (!graph[dep].dependents) {
          graph[dep].dependents = [];
        }
        if (!graph[dep].dependents.includes(fileName)) {
          graph[dep].dependents.push(fileName);
        }
      }
    });
  });
  
  const circular = findCircularDependencies(graph);
  const tree = buildDependencyTree(graph);
  
  // Debug output
  let totalDeps = 0;
  let filesWithDeps = 0;
  Object.keys(graph).forEach(key => {
    if (graph[key].dependencies.length > 0) {
      filesWithDeps++;
      totalDeps += graph[key].dependencies.length;
    }
  });
  
  self.postMessage({
    type: 'debug',
    message: \`Dependency stats: \${filesWithDeps} files have dependencies, \${totalDeps} total deps\`
  });
  
  return {
    graph,
    circular,
    tree
  };
}

function extractAllDependencies(code, ext, currentFile, allFiles) {
  if (!code) return [];
  
  const dependencies = [];
  
  // Get filename without extension for matching
  const baseName = currentFile.split('/').pop().replace(/\\.[^/.]+$/, '');
  const dirPath = currentFile.includes('/') ? 
    currentFile.substring(0, currentFile.lastIndexOf('/') + 1) : '';
  
  switch(ext) {
    case '.py': {
      // Python imports
      const importMatches = code.match(/^import\\s+(\\S+)/gm) || [];
      importMatches.forEach(m => {
        const match = m.match(/import\\s+(\\S+)/);
        if (match) {
          const parts = match[1].split('.');
          dependencies.push(parts[0]);
        }
      });
      
      const fromMatches = code.match(/^from\\s+(\\S+)\\s+import/gm) || [];
      fromMatches.forEach(m => {
        const match = m.match(/from\\s+(\\S+)\\s+import/);
        if (match) dependencies.push(match[1]);
      });
      
      // Relative imports
      const relativeMatches = code.match(/^from\\s+(\\.+)(\\S*)\\s+import/gm) || [];
      relativeMatches.forEach(m => {
        const match = m.match(/from\\s+(\\.+)(\\S*)\\s+import/);
        if (match) {
          const dots = match[1].length;
          const module = match[2];
          
          // Build relative path
          let parts = currentFile.split('/');
          parts.pop(); // Remove current file
          
          for (let j = 0; j < dots - 1; j++) {
            parts.pop(); // Go up for each dot beyond the first
          }
          
          if (module) {
            parts.push(module.replace(/^\\./, ''));
          }
          
          const possiblePath = parts.join('/') + '.py';
          if (allFiles.some(f => f.name === possiblePath)) {
            dependencies.push(possiblePath);
          }
        }
      });
      
      // Django-specific: model imports in views/serializers
      if (code.includes('import models') || code.includes('from .models')) {
        const possibleModelPath = dirPath + 'models.py';
        if (allFiles.some(f => f.name === possibleModelPath)) {
          dependencies.push(possibleModelPath);
        }
      }
      
      // Class inheritance detection
      const classMatches = code.match(/class\\s+(\\w+)\\s*\\(([^)]+)\\)/g) || [];
      classMatches.forEach(m => {
        const match = m.match(/class\\s+\\w+\\s*\\(([^)]+)\\)/);
        if (match) {
          const parents = match[1].split(',').map(p => p.trim());
          parents.forEach(parent => {
            // Check if parent is a class from another file
            const possibleFiles = allFiles.filter(f => {
              const content = f.content || '';
              return content.includes('class ' + parent + '\\\\b') || 
                     content.includes('class ' + parent + '(');
            });
            possibleFiles.forEach(f => {
              if (f.name !== currentFile) {
                dependencies.push(f.name);
              }
            });
          });
        }
      });
      
      // Django URL patterns
      if (currentFile.includes('urls.py')) {
        const urlMatches = code.match(/path\\(\\s*['"][^'"]+['"]\\s*,\\s*(\\w+)\\s*\\./g) || [];
        urlMatches.forEach(m => {
          const match = m.match(/path\\(\\s*['"][^'"]+['"]\\s*,\\s*(\\w+)\\s*\\./);
          if (match) {
            const viewModule = match[1];
            const possibleViewPath = dirPath + viewModule + '.py';
            if (allFiles.some(f => f.name === possibleViewPath)) {
              dependencies.push(possibleViewPath);
            }
          }
        });
        
        // include() in urls
        const includeMatches = code.match(/include\\(\\s*['"]([^'"]+)['"]\\s*\\)/g) || [];
        includeMatches.forEach(m => {
          const match = m.match(/include\\(\\s*['"]([^'"]+)['"]\\s*\\)/);
          if (match) {
            const includePath = match[1].replace(/\\./g, '/') + '/urls.py';
            const possibleUrlsPath = dirPath + includePath;
            if (allFiles.some(f => f.name === possibleUrlsPath)) {
              dependencies.push(possibleUrlsPath);
            }
          }
        });
      }
      
      // Django settings usage
      if (code.includes('settings.') && !currentFile.includes('settings.py')) {
        const possibleSettingsPath = 'settings.py';
        if (allFiles.some(f => f.name === possibleSettingsPath)) {
          dependencies.push(possibleSettingsPath);
        }
        
        // Also check for project settings
        const projectSettingsPath = dirPath.split('/')[0] + '/settings.py';
        if (allFiles.some(f => f.name === projectSettingsPath)) {
          dependencies.push(projectSettingsPath);
        }
      }
      
      // Admin registration
      if (currentFile.includes('admin.py')) {
        const modelMatches = code.match(/admin\\.site\\.register\\(\\s*(\\w+)\\s*\\)/g) || [];
        modelMatches.forEach(m => {
          const match = m.match(/admin\\.site\\.register\\(\\s*(\\w+)\\s*\\)/);
          if (match) {
            const modelName = match[1];
            const possibleModelsPath = dirPath + 'models.py';
            if (allFiles.some(f => f.name === possibleModelsPath)) {
              dependencies.push(possibleModelsPath);
            }
          }
        });
      }
      break;
    }
    
    case '.js':
    case '.ts':
    case '.jsx':
    case '.tsx': {
      // ES6 imports
      const importMatches = code.match(/import\\s+.*\\s+from\\s+['"]([^'"]+)['"]/g) || [];
      importMatches.forEach(m => {
        const match = m.match(/from\\s+['"]([^'"]+)['"]/);
        if (match) dependencies.push(match[1]);
      });
      
      // require statements
      const requireMatches = code.match(/require\\s*\\(\\s*['"]([^'"]+)['"]\\s*\\)/g) || [];
      requireMatches.forEach(m => {
        const match = m.match(/['"]([^'"]+)['"]/);
        if (match) dependencies.push(match[1]);
      });
      break;
    }
    
    case '.vue': {
      // Imports in script section
      const scriptSection = code.match(/<script>([\\s\\S]*?)<\\/script>/i);
      if (scriptSection && scriptSection[1]) {
        const script = scriptSection[1];
        const importMatches = script.match(/import\\s+.*\\s+from\\s+['"]([^'"]+)['"]/g) || [];
        importMatches.forEach(m => {
          const match = m.match(/from\\s+['"]([^'"]+)['"]/);
          if (match) dependencies.push(match[1]);
        });
        
        const requireMatches = script.match(/require\\s*\\(\\s*['"]([^'"]+)['"]\\s*\\)/g) || [];
        requireMatches.forEach(m => {
          const match = m.match(/['"]([^'"]+)['"]/);
          if (match) dependencies.push(match[1]);
        });
      }
      
      // Script with lang attribute
      const scriptLangSection = code.match(/<script\\s+lang=\\s*['"]\\w+['"]>([\\s\\S]*?)<\\/script>/i);
      if (scriptLangSection && scriptLangSection[1]) {
        const script = scriptLangSection[1];
        const importMatches = script.match(/import\\s+.*\\s+from\\s+['"]([^'"]+)['"]/g) || [];
        importMatches.forEach(m => {
          const match = m.match(/from\\s+['"]([^'"]+)['"]/);
          if (match) dependencies.push(match[1]);
        });
      }
      
      // Component usage in template
      const templateSection = code.match(/<template>([\\s\\S]*?)<\\/template>/i);
      if (templateSection && templateSection[1]) {
        const template = templateSection[1];
        // Find component tags (capitalized)
        const componentMatches = template.match(/<([A-Z]\\w+)/g) || [];
        componentMatches.forEach(tag => {
          const componentName = tag.substring(1);
          // Look for a .vue file with similar name
          const possibleComponentPath = dirPath + componentName + '.vue';
          if (allFiles.some(f => f.name === possibleComponentPath)) {
            dependencies.push(possibleComponentPath);
          }
        });
      }
      break;
    }
    
    case '.html': {
      // Script tags with src
      const scriptSrcMatches = code.match(/<script\\s+[^>]*src=['"]([^'"]+)['"]/gi) || [];
      scriptSrcMatches.forEach(m => {
        const match = m.match(/src=['"]([^'"]+)['"]/);
        if (match) dependencies.push(match[1]);
      });
      
      // Link tags for CSS
      const linkHrefMatches = code.match(/<link\\s+[^>]*href=['"]([^'"]+)['"]/gi) || [];
      linkHrefMatches.forEach(m => {
        const match = m.match(/href=['"]([^'"]+)['"]/);
        if (match && (m.includes('stylesheet') || m.includes('text/css'))) {
          dependencies.push(match[1]);
        }
      });
      
      // Template inheritance (Django/Jinja)
      const extendsMatches = code.match(/{%\\s*extends\\s+['"]([^'"]+)['"]/g) || [];
      extendsMatches.forEach(m => {
        const match = m.match(/['"]([^'"]+)['"]/);
        if (match) {
          const templatePath = match[1];
          // Try to find the template
          const possiblePaths = [
            templatePath,
            'templates/' + templatePath,
            dirPath + templatePath
          ];
          for (const path of possiblePaths) {
            if (allFiles.some(f => f.name.includes(path))) {
              dependencies.push(path);
              break;
            }
          }
        }
      });
      
      const includeMatches = code.match(/{%\\s*include\\s+['"]([^'"]+)['"]/g) || [];
      includeMatches.forEach(m => {
        const match = m.match(/['"]([^'"]+)['"]/);
        if (match) dependencies.push(match[1]);
      });
      break;
    }
    
    case '.css':
    case '.scss': {
      // CSS @import statements
      const cssImportMatches = code.match(/@import\\s+['"]([^'"]+)['"]/g) || [];
      cssImportMatches.forEach(m => {
        const match = m.match(/['"]([^'"]+)['"]/);
        if (match) dependencies.push(match[1]);
      });
      break;
    }
  }
  
  return [...new Set(dependencies.filter(d => d && d.trim()))];
}

function resolveDependencyPath(currentFile, dependency, fileMap, currentExt) {
  // Handle relative paths
  if (dependency.startsWith('./') || dependency.startsWith('../')) {
    const parts = currentFile.split('/');
    parts.pop(); // Remove current file
    
    const depParts = dependency.split('/');
    depParts.forEach(part => {
      if (part === '..') {
        parts.pop();
      } else if (part !== '.' && part !== '') {
        parts.push(part);
      }
    });
    
    const basePath = parts.join('/');
    
    // Try common extensions based on current file's extension
    let extensions = [];
    if (currentExt === '.py') {
      extensions = ['.py', ''];
    } else if (currentExt === '.js' || currentExt === '.ts') {
      extensions = ['.js', '.ts', '.jsx', '.tsx', '.json', ''];
    } else if (currentExt === '.html' || currentExt === '.htm') {
      extensions = ['.html', '.htm', ''];
    } else if (currentExt === '.css' || currentExt === '.scss') {
      extensions = ['.css', '.scss', ''];
    } else if (currentExt === '.vue') {
      extensions = ['.vue', '.js', '.ts', ''];
    } else {
      extensions = [currentExt, ''];
    }
    
    for (const ext of extensions) {
      const fullPath = basePath + ext;
      if (fileMap[fullPath]) return fullPath;
      
      // Try as directory with index file
      if (!ext) {
        if (currentExt === '.py') {
          const initPath = basePath + '/__init__.py';
          if (fileMap[initPath]) return initPath;
        } else if (currentExt === '.js' || currentExt === '.ts') {
          const indexPath = basePath + '/index' + currentExt;
          if (fileMap[indexPath]) return indexPath;
        }
      }
    }
    
    return null;
  }
  
  // For non-relative paths, try to find matching files
  const possibleMatches = Object.keys(fileMap).filter(f => 
    f.endsWith('/' + dependency) || 
    f.endsWith(dependency + currentExt) ||
    f.includes('/' + dependency + '/') ||
    f === dependency ||
    f === dependency + currentExt
  );
  
  if (possibleMatches.length > 0) {
    return possibleMatches[0];
  }
  
  return dependency;
}

function findCircularDependencies(graph) {
  const circular = [];
  const visited = new Set();
  const stack = [];
  
  const dfs = (node) => {
    if (stack.includes(node)) {
      const cycleStart = stack.indexOf(node);
      const cycle = stack.slice(cycleStart).concat(node);
      circular.push(cycle);
      return;
    }
    
    if (visited.has(node)) return;
    
    visited.add(node);
    stack.push(node);
    
    if (graph[node] && graph[node].dependencies) {
      graph[node].dependencies.forEach(dep => {
        dfs(dep);
      });
    }
    
    stack.pop();
  };
  
  Object.keys(graph).forEach(node => {
    dfs(node);
  });
  
  // Remove duplicates
  return circular.filter((cycle, index, self) => 
    index === self.findIndex(c => JSON.stringify(c) === JSON.stringify(cycle))
  );
}

function buildDependencyTree(graph) {
  const roots = Object.values(graph)
    .filter(file => !file.dependents || file.dependents.length === 0)
    .map(file => buildTreeNode(file, graph));
  
  return roots;
}

function buildTreeNode(file, graph, visited = new Set()) {
  if (visited.has(file.name)) {
    return { ...file, dependencies: [], circular: true };
  }
  
  const newVisited = new Set(visited);
  newVisited.add(file.name);
  
  const node = {
    name: file.name,
    dependencies: [],
    dependents: file.dependents || []
  };
  
  if (file.dependencies && file.dependencies.length > 0) {
    node.dependencies = file.dependencies
      .map(dep => {
        if (graph[dep]) {
          return buildTreeNode(graph[dep], graph, newVisited);
        }
        return { name: dep, dependencies: [], missing: true };
      })
      .filter(dep => dep);
  }
  
  return node;
}

// Helper function to find line number of a substring at a specific index
function findLineNumber(code, substring, startIndex = 0) {
  const lines = code.split('\\n');
  let currentPos = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineStart = currentPos;
    const lineEnd = currentPos + line.length;
    
    // Check if the substring starts within this line
    if (startIndex >= lineStart && startIndex < lineEnd) {
      return i + 1;
    }
    
    currentPos += line.length + 1; // +1 for the newline character
  }
  
  return 1; // fallback
}

// Helper function to get next few lines
function getNextLines(code, lineNum, count) {
  const lines = code.split('\\n');
  const result = [];
  for (let i = lineNum - 1; i < Math.min(lineNum - 1 + count, lines.length); i++) {
    result.push(lines[i]);
  }
  return result.join('\\n');
}

// ===== HELPER FUNCTIONS =====
function getFileExtension(filename) {
  if (!filename.includes('.')) {
    const specialFiles = ['Dockerfile', 'Makefile', '.gitignore', 'docker-compose.yml'];
    for (const special of specialFiles) {
      if (filename.includes(special)) return special;
    }
    return '';
  }
  return '.' + filename.split('.').pop().toLowerCase();
}

function getLanguageFromExt(ext) {
  const languageMap = {
    '.js': 'JavaScript',
    '.ts': 'TypeScript',
    '.py': 'Python',
    '.java': 'Java',
    '.html': 'HTML',
    '.css': 'CSS',
    '.scss': 'SCSS',
    '.vue': 'Vue',
    '.json': 'JSON',
    '.md': 'Markdown',
    '.xml': 'XML',
    '.yaml': 'YAML',
    '.yml': 'YAML',
    '.sh': 'Shell',
    '.c': 'C',
    '.cpp': 'C++',
    '.cs': 'C#',
    '.go': 'Go',
    '.php': 'PHP',
    '.rb': 'Ruby',
    'Dockerfile': 'Docker',
    'Makefile': 'Make',
    '.txt': 'Text'
  };
  return languageMap[ext] || 'Other';
}

function countLines(content, options, extension) {
  let lines = content.split('\\n');
  
  // Remove comments if enabled
  if (options.ignoreComments) {
    const pattern = COMMENT_PATTERNS[extension];
    if (pattern) {
      content = content.replace(pattern, '');
      lines = content.split('\\n');
    }
  }
  
  // Filter empty lines if not counting them
  if (!options.countEmptyLines) {
    lines = lines.filter(line => line.trim().length > 0);
  }
  
  return lines.length;
}

function countFunctions(code, ext) {
  if (!code) return 0;
  
  switch(ext) {
    case '.js':
    case '.ts': {
      const funcs = (code.match(/\\bfunction\\s+\\w+/g) || []).length;
      const arrows = (code.match(/\\bconst\\s+\\w+\\s*=\\s*\\(/g) || []).length;
      const methods = (code.match(/\\b\\w+\\s*:\\s*function/g) || []).length;
      const classMethods = (code.match(/\\b\\w+\\s*\\([^)]*\\)\\s*{/g) || []).length;
      return funcs + arrows + methods + Math.floor(classMethods / 2);
    }
    case '.py':
      return (code.match(/\\bdef\\s+\\w+/g) || []).length;
    case '.java':
    case '.cpp':
    case '.c':
    case '.cs':
      return (code.match(/(public|private|protected)?\\s+\\w+\\s+\\w+\\s*\\([^)]*\\)\\s*{/g) || []).length;
    case '.php':
      return (code.match(/function\\s+\\w+/g) || []).length;
    case '.rb':
      return (code.match(/def\\s+\\w+/g) || []).length;
    case '.go':
      return (code.match(/func\\s+\\w+/g) || []).length;
    default:
      return 0;
  }
}

function countClasses(code, ext) {
  if (!code) return 0;
  
  switch(ext) {
    case '.js':
    case '.ts':
    case '.py':
    case '.java':
    case '.cpp':
    case '.cs':
    case '.php':
      return (code.match(/\\bclass\\s+\\w+/g) || []).length;
    default:
      return 0;
  }
}

function extractImports(code, ext) {
  if (!code) return [];
  
  const imports = [];
  
  switch(ext) {
    case '.js':
    case '.ts': {
      const es6Matches = code.match(/import\\s+.*\\s+from\\s+['"]([^'"]+)['"]/g) || [];
      es6Matches.forEach(m => {
        const match = m.match(/from\\s+['"]([^'"]+)['"]/);
        if (match) imports.push(match[1]);
      });
      
      const requireMatches = code.match(/require\\s*\\(\\s*['"]([^'"]+)['"]\\s*\\)/g) || [];
      requireMatches.forEach(m => {
        const match = m.match(/['"]([^'"]+)['"]/);
        if (match) imports.push(match[1]);
      });
      break;
    }
      
    case '.py': {
      const pyMatches = code.match(/^(?:from\\s+(\\S+)\\s+)?import\\s+(\\S+)/gm) || [];
      pyMatches.forEach(m => {
        if (m.startsWith('from')) {
          const match = m.match(/from\\s+(\\S+)\\s+import/);
          if (match) imports.push(match[1]);
        } else {
          const match = m.match(/import\\s+(\\S+)/);
          if (match) imports.push(match[1]);
        }
      });
      break;
    }
      
    case '.vue': {
      const vueMatches = code.match(/import\\s+.*\\s+from\\s+['"]([^'"]+)['"]/g) || [];
      vueMatches.forEach(m => {
        const match = m.match(/from\\s+['"]([^'"]+)['"]/);
        if (match) imports.push(match[1]);
      });
      break;
    }
  }
  
  return imports;
}

function extractTodoComments(code, fileName) {
  const todos = [];
  const lines = code.split('\\n');
  
  lines.forEach((line, index) => {
    const todoMatch = line.match(/\\/\\/\\s*(TODO|FIXME|HACK):?\\s*(.+)/i);
    if (todoMatch) {
      todos.push({
        type: todoMatch[1].toUpperCase(),
        comment: todoMatch[2].trim(),
        file: fileName,
        line: index + 1
      });
    }
  });
  
  return todos;
}
`;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
}